import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Plus, Trash2, X } from 'lucide-react';
import type { ProductCategory } from '@kick/types';
import { UPLOAD } from '@kick/types';
import { createProductSchema } from '@kick/validation';
import { getErrorDetails, getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import {
  useGuardarProducto,
  useProducto,
  useSubirImagenProducto,
} from '@/lib/queries';
import {
  AreaTexto,
  Boton,
  Campo,
  Cargando,
  Entrada,
  EntradaDinero,
  Interruptor,
  Modal,
  Seleccion
} from '@/components/ui';
import { useAvisos } from '@/components/avisos';

/** Grupo de modificadores mientras se edita, antes de enviarlo. */
interface GrupoBorrador {
  key: string;
  name: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  options: { key: string; name: string; priceDelta: number }[];
}

const nuevaClave = (): string => Math.random().toString(36).slice(2, 10);

export function FormularioProducto({
  merchantId,
  productoId,
  categoryIdInicial,
  secciones,
  onCerrar,
}: {
  merchantId: string;
  productoId?: string;
  categoryIdInicial: string;
  secciones: ProductCategory[];
  onCerrar: () => void;
}) {
  const avisos = useAvisos();
  const existente = useProducto(merchantId, productoId ?? null);
  const guardar = useGuardarProducto(merchantId);
  const subirImagen = useSubirImagenProducto(merchantId);

  const [categoryId, setCategoryId] = useState(categoryIdInicial);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(0);
  const [compareAtPrice, setCompareAtPrice] = useState(0);
  const [prepMinutes, setPrepMinutes] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [grupos, setGrupos] = useState<GrupoBorrador[]>([]);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const [archivo, setArchivo] = useState<File | null>(null);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const inputArchivo = useRef<HTMLInputElement>(null);

  /** Carga los datos cuando se está editando un producto existente. */
  useEffect(() => {
    if (!existente.data) return;
    const p = existente.data;
    setCategoryId(p.categoryId);
    setName(p.name);
    setDescription(p.description ?? '');
    setPrice(p.price);
    setCompareAtPrice(p.compareAtPrice ?? 0);
    setPrepMinutes(p.prepMinutes ? String(p.prepMinutes) : '');
    setIsAvailable(p.isAvailable);
    setGrupos(
      p.optionGroups.map((g) => ({
        key: nuevaClave(),
        name: g.name,
        minSelect: g.minSelect,
        maxSelect: g.maxSelect,
        isRequired: g.isRequired,
        options: g.options.map((o) => ({
          key: nuevaClave(),
          name: o.name,
          priceDelta: o.priceDelta,
        })),
      })),
    );
  }, [existente.data]);

  /** La vista previa es un objeto en memoria: hay que liberarlo al cambiarla. */
  useEffect(() => {
    if (!archivo) return;
    const url = URL.createObjectURL(archivo);
    setVistaPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [archivo]);

  const elegirArchivo = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const elegido = e.target.files?.[0];
    if (!elegido) return;

    // Se valida aquí también para dar respuesta inmediata; el servidor
    // vuelve a comprobarlo porque esta validación es solo cortesía.
    if (!UPLOAD.allowedImageTypes.includes(elegido.type as never)) {
      avisos.error('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }
    if (elegido.size > UPLOAD.maxImageBytes) {
      avisos.error(`La imagen no puede superar ${UPLOAD.maxImageBytes / 1024 / 1024} MB`);
      return;
    }
    setArchivo(elegido);
  };

  const enviar = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErrores({});

    const datos = {
      categoryId,
      name: name.trim(),
      description: description.trim() || undefined,
      price,
      compareAtPrice: compareAtPrice > 0 ? compareAtPrice : null,
      prepMinutes: prepMinutes ? Number(prepMinutes) : null,
      isAvailable,
      optionGroups: grupos.map((g, i) => ({
        name: g.name.trim(),
        minSelect: g.minSelect,
        maxSelect: g.maxSelect,
        isRequired: g.isRequired,
        sortOrder: i,
        options: g.options.map((o, j) => ({
          name: o.name.trim(),
          priceDelta: o.priceDelta,
          isAvailable: true,
          sortOrder: j,
        })),
      })),
    };

    // Mismo esquema que valida la API: los mensajes coinciden exactamente.
    const revision = createProductSchema.safeParse(datos);
    if (!revision.success) {
      const encontrados: Record<string, string> = {};
      for (const issue of revision.error.issues) {
        const campo = issue.path.join('.');
        if (!encontrados[campo]) encontrados[campo] = issue.message;
      }
      setErrores(encontrados);
      avisos.error('Revisa los campos marcados');
      return;
    }

    guardar.mutate(
      { ...(productoId ? { id: productoId } : {}), ...datos },
      {
        onSuccess: async (producto) => {
          // La imagen se sube después: necesita el id del producto ya creado.
          if (archivo) {
            try {
              await subirImagen.mutateAsync({ id: producto.id, archivo });
            } catch (error) {
              avisos.error(`Producto guardado, pero la imagen falló: ${getErrorMessage(error)}`);
              onCerrar();
              return;
            }
          }
          avisos.exito(productoId ? 'Producto actualizado' : 'Producto creado');
          onCerrar();
        },
        onError: (error) => {
          setErrores(
            Object.fromEntries(
              Object.entries(getErrorDetails(error)).map(([k, v]) => [k, v[0] ?? '']),
            ),
          );
          avisos.error(getErrorMessage(error));
        },
      },
    );
  };

  const cargando = !!productoId && existente.isLoading;
  const imagenActual = vistaPrevia ?? existente.data?.imageUrl ?? null;

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={productoId ? 'Editar producto' : 'Nuevo producto'}
      descripcion={productoId ? undefined : 'Los cambios se verán en la tienda al instante.'}
      ancho="lg"
    >
      {cargando ? (
        <Cargando />
      ) : (
        <form onSubmit={enviar} className="max-h-[75vh] overflow-y-auto p-5">
          <div className="grid gap-5 sm:grid-cols-[180px_1fr]">
            {/* Imagen */}
            <div>
              <input
                ref={inputArchivo}
                type="file"
                accept={UPLOAD.allowedImageTypes.join(',')}
                onChange={elegirArchivo}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => inputArchivo.current?.click()}
                className={cn(
                  'group relative grid aspect-square w-full place-items-center overflow-hidden rounded-kick border-2 border-dashed transition',
                  imagenActual
                    ? 'border-transparent'
                    : 'border-kick-gray/30 hover:border-kick-red hover:bg-kick-red/5',
                )}
              >
                {imagenActual ? (
                  <>
                    <img src={imagenActual} alt="" className="h-full w-full bg-kick-light object-contain" />
                    <span className="absolute inset-0 grid place-items-center bg-black/50 text-sm font-semibold text-white opacity-0 transition group-hover:opacity-100">
                      Cambiar foto
                    </span>
                  </>
                ) : (
                  <span className="flex flex-col items-center gap-2 text-kick-gray">
                    <ImagePlus className="h-7 w-7" />
                    <span className="text-sm font-semibold">Agregar foto</span>
                  </span>
                )}
              </button>

              {archivo && (
                <button
                  type="button"
                  onClick={() => setArchivo(null)}
                  className="mt-2 w-full text-xs text-kick-gray transition hover:text-red-600"
                >
                  Quitar la imagen seleccionada
                </button>
              )}
            </div>

            {/* Datos básicos */}
            <div className="space-y-4">
              <Campo etiqueta="Nombre" requerido error={errores.name}>
                <Entrada
                  id="name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Bandeja paisa completa"
                  maxLength={150}
                  error={!!errores.name}
                />
              </Campo>

              <Campo etiqueta="Descripción" error={errores.description}>
                <AreaTexto
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Frijoles, arroz, carne molida, chicharrón…"
                  maxLength={1000}
                />
              </Campo>

              <div className="grid gap-4 sm:grid-cols-3">
                <Campo etiqueta="Precio" requerido error={errores.price}>
                  <EntradaDinero centavos={price} onCambio={setPrice} error={!!errores.price} />
                </Campo>

                <Campo
                  etiqueta="Precio tachado"
                  error={errores.compareAtPrice}
                  ayuda={compareAtPrice > 0 ? 'Se muestra tachado' : 'Opcional'}
                >
                  <EntradaDinero
                    centavos={compareAtPrice}
                    onCambio={setCompareAtPrice}
                    error={!!errores.compareAtPrice}
                  />
                </Campo>

                <Campo etiqueta="Preparación" ayuda="Minutos">
                  <Entrada
                    type="number"
                    min={1}
                    max={180}
                    value={prepMinutes}
                    onChange={(e) => setPrepMinutes(e.target.value)}
                    placeholder="20"
                  />
                </Campo>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo etiqueta="Sección" requerido error={errores.categoryId}>
                  <Seleccion
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    error={!!errores.categoryId}
                  >
                    {secciones.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Seleccion>
                </Campo>

                <Campo etiqueta="Disponibilidad">
                  <div className="flex items-center gap-3 py-2.5">
                    <Interruptor
                      activo={isAvailable}
                      etiqueta="Disponible"
                      onCambio={setIsAvailable}
                    />
                    <span className="text-sm text-kick-ink">
                      {isAvailable ? 'Disponible' : 'Agotado'}
                    </span>
                  </div>
                </Campo>
              </div>
            </div>
          </div>

          <EditorModificadores grupos={grupos} onCambio={setGrupos} errores={errores} />

          <div className="sticky bottom-0 -mx-5 -mb-5 mt-6 flex justify-end gap-2 border-t border-kick-gray/15 bg-white px-5 py-4">
            <Boton type="button" variante="secundario" onClick={onCerrar}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardar.isPending || subirImagen.isPending}>
              {productoId ? 'Guardar cambios' : 'Crear producto'}
            </Boton>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ── Modificadores ───────────────────────────────────────────

function EditorModificadores({
  grupos,
  onCambio,
  errores,
}: {
  grupos: GrupoBorrador[];
  onCambio: (grupos: GrupoBorrador[]) => void;
  errores: Record<string, string>;
}) {
  const actualizar = (indice: number, cambios: Partial<GrupoBorrador>): void => {
    onCambio(grupos.map((g, i) => (i === indice ? { ...g, ...cambios } : g)));
  };

  const agregarGrupo = (): void => {
    onCambio([
      ...grupos,
      {
        key: nuevaClave(),
        name: '',
        minSelect: 1,
        maxSelect: 1,
        isRequired: true,
        options: [{ key: nuevaClave(), name: '', priceDelta: 0 }],
      },
    ]);
  };

  return (
    <div className="mt-6 border-t border-kick-gray/15 pt-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-kick-ink">Opciones del producto</h3>
          <p className="text-sm text-kick-gray">
            Tamaños, adiciones, punto de la carne… El cliente las elige al pedir.
          </p>
        </div>
        <Boton
          type="button"
          variante="secundario"
          icono={<Plus className="h-4 w-4" />}
          onClick={agregarGrupo}
        >
          Grupo
        </Boton>
      </div>

      {grupos.length === 0 ? (
        <p className="mt-4 rounded-kick border border-dashed border-kick-gray/30 px-4 py-6 text-center text-sm text-kick-gray">
          Sin opciones. Este producto se pedirá tal cual.
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          {grupos.map((grupo, indice) => (
            <fieldset key={grupo.key} className="rounded-kick border border-kick-gray/20 p-4">
              <div className="flex flex-wrap items-end gap-3">
                <Campo etiqueta="Nombre del grupo" className="min-w-[180px] flex-1">
                  <Entrada
                    value={grupo.name}
                    onChange={(e) => actualizar(indice, { name: e.target.value })}
                    placeholder="Tamaño"
                    error={!!errores[`optionGroups.${indice}.name`]}
                  />
                </Campo>

                <Campo etiqueta="Mínimo" className="w-24">
                  <Entrada
                    type="number"
                    min={0}
                    max={grupo.options.length}
                    value={grupo.minSelect}
                    onChange={(e) => actualizar(indice, { minSelect: Number(e.target.value) })}
                  />
                </Campo>

                <Campo etiqueta="Máximo" className="w-24">
                  <Entrada
                    type="number"
                    min={1}
                    max={grupo.options.length}
                    value={grupo.maxSelect}
                    onChange={(e) => actualizar(indice, { maxSelect: Number(e.target.value) })}
                  />
                </Campo>

                <div className="flex items-center gap-2 pb-2.5">
                  <Interruptor
                    activo={grupo.isRequired}
                    etiqueta="Obligatorio"
                    onCambio={(obligatorio) =>
                      actualizar(indice, {
                        isRequired: obligatorio,
                        // Un grupo obligatorio debe exigir al menos una opción.
                        minSelect: obligatorio ? Math.max(1, grupo.minSelect) : 0,
                      })
                    }
                  />
                  <span className="text-sm text-kick-ink">Obligatorio</span>
                </div>

                <button
                  type="button"
                  onClick={() => onCambio(grupos.filter((_, i) => i !== indice))}
                  aria-label="Eliminar grupo"
                  className="mb-1 rounded-kick p-2 text-kick-gray transition hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {errores[`optionGroups.${indice}.minSelect`] && (
                <p className="mt-1 text-sm text-red-600">
                  {errores[`optionGroups.${indice}.minSelect`]}
                </p>
              )}
              {errores[`optionGroups.${indice}.maxSelect`] && (
                <p className="mt-1 text-sm text-red-600">
                  {errores[`optionGroups.${indice}.maxSelect`]}
                </p>
              )}

              <div className="mt-3 space-y-2">
                {grupo.options.map((opcion, j) => (
                  <div key={opcion.key} className="flex items-center gap-2">
                    <Entrada
                      value={opcion.name}
                      onChange={(e) =>
                        actualizar(indice, {
                          options: grupo.options.map((o, k) =>
                            k === j ? { ...o, name: e.target.value } : o,
                          ),
                        })
                      }
                      placeholder="Nombre de la opción"
                      className="flex-1"
                    />
                    <div className="w-36">
                      <EntradaDinero
                        centavos={opcion.priceDelta}
                        onCambio={(centavos) =>
                          actualizar(indice, {
                            options: grupo.options.map((o, k) =>
                              k === j ? { ...o, priceDelta: centavos } : o,
                            ),
                          })
                        }
                        placeholder="0"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={grupo.options.length === 1}
                      onClick={() =>
                        actualizar(indice, { options: grupo.options.filter((_, k) => k !== j) })
                      }
                      aria-label="Quitar opción"
                      className="rounded-kick p-2 text-kick-gray transition hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-kick-gray"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() =>
                    actualizar(indice, {
                      options: [...grupo.options, { key: nuevaClave(), name: '', priceDelta: 0 }],
                    })
                  }
                  className="text-sm font-semibold text-kick-red transition hover:text-kick-red-dark"
                >
                  + Agregar opción
                </button>
              </div>

              {grupo.options.some((o) => o.priceDelta > 0) && (
                <p className="mt-3 text-xs text-kick-gray">
                  Con la opción más cara, el producto sube{' '}
                  {formatCOP(Math.max(...grupo.options.map((o) => o.priceDelta)))}.
                </p>
              )}
            </fieldset>
          ))}
        </div>
      )}
    </div>
  );
}

/** Se exporta el icono de carga para reutilizarlo en pantallas hermanas. */
export { Loader2 as IconoCargando };
